import {
  OidcPkceProvider,
  OktaOidcPkceProvider,
} from "@p0security/shared/types/tenant/sso";
import { deferral } from "@p0security/shared/util/deferral";
import {
  Auth,
  OAuthProvider,
  UserCredential,
  signInWithCredential,
} from "firebase/auth";
import pkceChallenge from "pkce-challenge";
import { useLocation } from "react-router";
import { assertNever } from "utils/assert";
import { getEnvironment } from "utils/environment";

import { TOKEN_EXPIRATION_KEY } from "../Session/useSessionTimeout";

const oktaAuthServerUrl = ({
  providerDomain,
  authServerPath,
}: OktaOidcPkceProvider) => {
  const asPath = authServerPath ? `${authServerPath}/v1` : "v1";
  return `https://${providerDomain}/oauth2/${asPath}`;
};

const authUrl = (tenant: OidcPkceProvider) =>
  tenant.providerType === "okta"
    ? oktaAuthServerUrl(tenant)
    : tenant.providerType === "ping"
      ? `https://${tenant.providerDomain}/${tenant.environmentId}/as`
      : tenant.providerType === "cloudflare"
        ? `https://${tenant.providerDomain}/cdn-cgi/access/sso/oidc/${tenant.clientId}`
        : tenant.providerType === "duo"
          ? `https://${tenant.providerDomain}/oidc/${tenant.clientId}`
          : assertNever(tenant);
// Mounted at oidc/auth/_redirect
export const OidcRedirect: React.FC<object> = () => {
  const { search } = useLocation();
  const { error } = (window.opener as any).p0.relayPkce(
    new URLSearchParams(search)
  );
  if (error) return <div>{error}</div>;
  window.close();
  return <></>;
};

export const signInWithPkce = async (
  auth: Auth,
  provider: OAuthProvider,
  tenant: OidcPkceProvider,
  orgSlug: string
): Promise<UserCredential> => {
  const codeDeferral = deferral<string>();

  const state = crypto.randomUUID();
  const base = authUrl(tenant);
  const { code_challenge, code_verifier } = pkceChallenge();
  const redirect_uri = new URL(`oidc/auth/_redirect`, window.location.origin)
    .href;

  const data = {
    client_id: tenant.clientId,
    code_challenge,
    code_challenge_method: "S256",
    redirect_uri,
    response_type: "code",
    scope: "openid groups profile email",
    state,
  };
  const search = new URLSearchParams(data);

  (window as any).p0 = {
    relayPkce: (search: URLSearchParams) => {
      if (search.get("state") !== state) {
        codeDeferral.reject("Code exchange state mismatch");
        return { error: "Code exchange state mismatch" };
      }
      const code = search.get("code");
      if (code) {
        codeDeferral.resolve(code);
      } else {
        codeDeferral.reject("No code returned");
      }
      return { ok: true };
    },
  };
  const authorizePath =
    tenant.providerType === "cloudflare" ? "authorization" : "authorize";

  window.open(
    `${base}/${authorizePath}?${search.toString()}`,
    "p0_oidc_signin",
    "popup=true"
  );

  const code = await codeDeferral.promise;
  const { apiUrl } = getEnvironment();

  // TODO: Should be useApiFetch
  const response = await fetch(`${apiUrl}/o/${orgSlug}/oidc/token`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      client_id: data.client_id,
      code,
      code_verifier,
      grant_type: "authorization_code",
      redirect_uri: data.redirect_uri,
      scope: data.scope,
      ...(tenant.providerType === "cloudflare"
        ? { client_secret: tenant.clientSecret }
        : {}),
    }).toString(),
  });

  if (!response?.ok) {
    throw new Error(await response.text());
  }

  const token = await response.json();

  // Extract expires_in (in seconds) and convert to milliseconds in UTC
  if (token.expires_in && typeof token.expires_in === "number") {
    const tokenExpiration = Date.now() + token.expires_in * 1000;
    sessionStorage.setItem(TOKEN_EXPIRATION_KEY, tokenExpiration.toString());
  }

  const credential = await signInWithCredential(
    auth,
    provider.credential({
      accessToken: token.access_token,
      idToken: token.id_token,
    })
  );
  return credential;
};
